import type { AccessTier } from './AccessTier'
import { AccessDeniedError } from './AccessTier'
import type { AccessGateConfig } from './createAccessGatedApi'

/**
 * Gates a streaming (AsyncIterable-returning) method without breaking the
 * `for await` contract. `createAccessGatedApi` cannot wrap streaming methods
 * because it returns Promises, and a `Promise<AsyncIterable>` is not itself
 * iterable. Instead this returns a fresh AsyncIterable whose iterator runs the
 * access check on first iteration — prompting (and resolving sign-in) BEFORE
 * the underlying stream is started, so the host opens the stream with the
 * now-authenticated token. On denial it throws and never touches the stream.
 */
export function gateStreaming<TArgs extends unknown[], TChunk>(
  streamFn: (...args: TArgs) => AsyncIterable<TChunk>,
  config: AccessGateConfig,
  requiredTier: AccessTier = 'authenticated_18plus',
): (...args: TArgs) => AsyncIterable<TChunk> {
  return (...args: TArgs): AsyncIterable<TChunk> => ({
    async *[Symbol.asyncIterator](): AsyncGenerator<TChunk> {
      if (config.isAnonymous()) {
        if (!config.isAutoPromptEnabled()) {
          throw new AccessDeniedError(requiredTier, 'prompt_login')
        }
        const result = await config.promptLogin()
        if (!result.success) {
          throw new AccessDeniedError(requiredTier, 'prompt_login')
        }
      }
      yield* streamFn(...args)
    },
  })
}
